import React from 'react';
import { ArrowLeft, Calendar, Clock, User, Share2, CheckCircle2, ArrowRight } from 'lucide-react';
import { blogData } from '../data/blogData';

interface BlogPostPageProps {
  slug: string;
  onNavigate: (path: string) => void;
  onOpenConsultation: () => void;
}

export const BlogPostPage: React.FC<BlogPostPageProps> = ({ slug, onNavigate, onOpenConsultation }) => {
  const post = blogData.find(p => p.slug === slug) || blogData[0];
  const related = blogData.filter(p => p.slug !== post.slug);

  return (
    <article className="bg-[#071426] text-[#D5DFEC] py-10 space-y-12">
      
      {/* Top Navigation */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => onNavigate('/blog')}
          className="inline-flex items-center gap-2 text-xs font-semibold text-[#8FA0B8] hover:text-white transition-colors mb-6"
        >
          <ArrowLeft size={14} />
          <span>Back to All Articles</span>
        </button>

        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <span>{post.category}</span>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white font-display tracking-tight leading-tight">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-xs text-[#8FA0B8] pt-2 border-b border-[#1b2f4a] pb-6">
            <span className="flex items-center gap-2 text-white font-medium">
              <User size={14} className="text-[#159DE8]" />
              <span>{post.author}</span>
            </span>
            <span>•</span>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} />
              {post.publishDate}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} />
              {post.readTime}
            </span>
          </div>
        </div>
      </div>

      {/* Featured Banner */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl overflow-hidden border border-[#1b2f4a] bg-[#050A13] max-h-96">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover max-h-96"
          />
        </div>
      </div>

      {/* Article Content & Takeaways */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Key Takeaways Box */}
        {post.keyTakeaways && (
          <div className="p-6 rounded-2xl bg-[#0c1a2d] border border-[#159DE8]/30 space-y-3">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#159DE8]" />
              <span>Key Executive Takeaways</span>
            </h3>
            <div className="space-y-2">
              {post.keyTakeaways.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-[#D5DFEC]">
                  <CheckCircle2 size={16} className="text-[#00C853] mt-0.5 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Paragraphs */}
        <div className="space-y-6 text-sm sm:text-base leading-relaxed text-[#D5DFEC]">
          {post.content.map((paragraph, idx) => (
            <p key={idx} className="leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>

        {/* Share & Author Card */}
        <div className="p-6 rounded-2xl bg-[#050A13] border border-[#1b2f4a] flex flex-col sm:flex-row items-center justify-between gap-4 mt-12">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-full bg-[#0B2342] text-[#159DE8] flex items-center justify-center font-bold text-base border border-[#1b2f4a]">
              {post.author.charAt(0)}
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">{post.author}</h4>
              <p className="text-xs text-[#8FA0B8]">Technology & Engineering Strategy at EBON Media Solutions</p>
            </div>
          </div>

          <button
            onClick={() => {
              if (navigator.clipboard) {
                navigator.clipboard.writeText(window.location.href);
                alert('Article URL copied to clipboard!');
              }
            }}
            className="px-4 py-2 rounded-lg bg-[#0c1a2d] border border-[#1b2f4a] text-xs text-[#D5DFEC] hover:text-white hover:border-[#159DE8] flex items-center gap-1.5 transition-colors"
          >
            <Share2 size={13} />
            <span>Share Article</span>
          </button>
        </div>

      </div>

      {/* Related Articles */}
      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 border-t border-[#1b2f4a]">
          <h3 className="text-xl font-bold text-white font-display mb-6">Related Insights</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {related.slice(0, 2).map((r) => (
              <div
                key={r.id}
                onClick={() => onNavigate(`/blog/${r.slug}`)}
                className="p-6 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] hover:border-[#159DE8] transition-all cursor-pointer group space-y-2"
              >
                <span className="text-[11px] font-semibold text-[#FF851B] uppercase tracking-wider">{r.category}</span>
                <h4 className="text-base font-bold text-white group-hover:text-[#FF851B] transition-colors">{r.title}</h4>
                <p className="text-xs text-[#8FA0B8] line-clamp-2">{r.summary}</p>
                <div className="pt-2 flex items-center gap-1 text-xs text-[#159DE8] font-semibold">
                  <span>Read Post</span>
                  <ArrowRight size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Consultation Bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-r from-[#0B2342] to-[#071426] border border-[#1b2f4a] flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-white font-display">Need expert architectural consultation?</h2>
            <p className="text-sm text-[#D5DFEC]">Schedule a dedicated discussion with our technology strategists.</p>
          </div>
          <button
            onClick={onOpenConsultation}
            className="px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-sm font-bold shadow-lg hover:shadow-glow-orange transition-all shrink-0 flex items-center gap-2"
          >
            <span>Book Consultation</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

    </article>
  );
};
